import { yupResolver } from "@hookform/resolvers/yup";
import { Button, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { useEventSession } from "../../providers/eventSessions";
import { IDynamicValues } from "../../providers/notifications/notifications.interface";
import { useTag } from "../../providers/tags/index";
import { useUser } from "../../providers/users";
import DynamicValuesContainer, {
  calculateExpirationTime,
} from "../DynamicValuesContainer";
import HourRelationContainer from "../HoursRelationContainer";
import HtmEditor from "../HtmlEditor";
import ModalFormBase from "../ModalFormBase";
import NotificationTags from "../NotificationTags";
import { Container } from "./styles";
import { toast } from "react-toastify";
import { useNotification } from "../../providers/notifications";

interface IProps {
  setModalAddEmail: React.Dispatch<React.SetStateAction<boolean>>;
  hourRelation: string;
}

const ModalAddEmail = ({ setModalAddEmail, hourRelation }: IProps) => {
  const { apiLoading } = useUser();
  const { createEmailNotification } = useEventSession();
  const { session_id } = useParams();
  const { sendSampleTestEmail } = useNotification();
  const { t } = useTranslation();
  const [valueContent, setValueContent] = useState("");
  const [toRegistrants, setToRegistrants] = useState("all");
  const { getAllTags, allTags } = useTag();
  const [tags, setTags] = useState<string[]>([]);
  const [tagsExclude, setTagsExclude] = useState<string[]>([]);
  const [dynamicValues, setDynamicValues] = useState<IDynamicValues[]>([]);
  const [emailTest, setEmailTest] = useState("");
  const valueChanged = useCallback(
    (e: { value?: string }) => {
      if (!e.value) {
        return;
      }
      setValueContent(e.value);
    },
    [setValueContent]
  );

  useEffect(() => {
    if (!allTags) {
      getAllTags();
    }
  }, []);

  const formSchema = yup
    .object()
    .shape({
      subject: yup
        .string()
        .required(t("field_required"))
        .max(128, t("field_max")),
      hoursInput: yup
        .number()
        .integer()
        .moreThan(-1)
        .typeError(t("field_required")),
      minutes: yup
        .number()
        .max(59)
        .integer()
        .moreThan(-1)
        .typeError(t("field_required")),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({ resolver: yupResolver(formSchema) });

  const onSubmit = async (data: any) => {
    data.hours = data.hoursInput * 3600000 + data.minutes * 60000;
    data.hourRelation = hourRelation;
    delete data.hoursInput;
    delete data.minutes;
    data.enabled = true;
    data.sessionId = session_id;
    data.html = valueContent;
    if (data.hourRelation === "on") data.hours = 0;

    data.dynamicValues = calculateExpirationTime(dynamicValues);

    await createEmailNotification(data);
    setModalAddEmail(false);
  };

  return (
    <ModalFormBase
      title="Email Notification"
      setModal={setModalAddEmail}
      onSubmit={handleSubmit(onSubmit)}
      apiLoading={apiLoading}
    >
      <Container>
        <HourRelationContainer
          hourRelation={hourRelation}
          register={register}
          errors={errors}
          notificationType="email"
        />

        <NotificationTags
          tags={tags}
          setTags={setTags}
          tagsExclude={tagsExclude}
          setTagsExclude={setTagsExclude}
          register={register}
          allTags={allTags}
          toRegistrants={toRegistrants}
          setToRegistrants={setToRegistrants}
        />
        <h3 className="smsSettings">Email Settings</h3>

        <p>Email subject</p>

        <TextField
          error={errors.subject === undefined ? false : true}
          helperText={errors.subject?.message?.toString()}
          id="standard-email"
          label="Enter an email subject"
          autoComplete="email"
          variant="filled"
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
            textAlign: "center",
            width: "100%",
            backgroundColor: "#FFF",
            "& fieldset": { border: "none" },
          }}
          {...register("subject")}
        />

        <p>Email body</p>

        <HtmEditor
          valueChanged={valueChanged}
          setValueContent={setValueContent}
          valueContent={valueContent}
        />

        {!["on"].includes(hourRelation) && (
          <DynamicValuesContainer
            dynamicValues={dynamicValues}
            setDynamicValues={setDynamicValues}
            hourRelation={hourRelation}
          />
        )}

        {valueContent && (
          <div className="testDiv">
            <TextField
              id="standard-emailFrom"
              label="Send test email"
              placeholder="youremail@email.com"
              variant="filled"
              InputProps={{
                disableUnderline: true,
              }}
              type="email"
              value={emailTest}
              onChange={(e) => setEmailTest(e.target.value)}
              sx={{
                boxShadow: "rgba(3,3,3,0.1) 2px 2px 4px ",
                width: "100%",
                textAlign: "center",
                backgroundColor: "#FFF",
                "& fieldset": { border: "none" },
              }}
            />
            <Button
              onClick={() => {
                if (!emailTest || !getValues("subject")) {
                  toast.error("Subject is required");
                  return;
                }
                if (!getValues("subject")) {
                  toast.error("Subject is required");
                  return;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(emailTest)) {
                  return;
                }

                sendSampleTestEmail(
                  emailTest,
                  getValues("subject"),
                  valueContent
                );
              }}
              sx={{ borderRadius: "25px", width: 150, ml: 1 }}
              variant="contained"
              disabled={apiLoading || !emailTest}
              size="small"
            >
              Send
            </Button>
          </div>
        )}
      </Container>
    </ModalFormBase>
  );
};

export default ModalAddEmail;
